import { fmtINR } from "@/lib/api";
import { Building2 } from "lucide-react";

function Row({ label, value, bold, accent }) {
  return (
    <div className={`flex justify-between py-2 px-3 ${bold ? "bg-slate-50 dark:bg-slate-800/60 font-semibold" : ""}`}>
      <span className={accent ? "text-emerald-700 dark:text-emerald-400" : ""}>{label}</span>
      <span className={`font-mono ${accent ? "text-emerald-700 dark:text-emerald-400" : ""}`}>{value}</span>
    </div>
  );
}

const EMPTY_BREAKDOWN = {
  earnings: { basic: 0, hra: 0, special_allowance: 0 },
  employer_contributions: { pf_employer: 0, health_insurance: 0, esi_employer: 0 },
  deductions: { pf_employee: 0, esi_employee: 0, professional_tax: 0, tds: 0 },
  monthly: { professional_tax: 0, tds: 0 },
  gross_annual: 0, net_annual: 0, net_monthly: 0, annual_ctc: 0, tax_regime: "new",
};

export default function OfferLetter({ offer }) {
  if (!offer) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground" data-testid="offer-letter-container">
        No offer letter data is available.
      </div>
    );
  }
  const raw = offer.breakdown || {};
  const b = {
    ...EMPTY_BREAKDOWN,
    ...raw,
    earnings: { ...EMPTY_BREAKDOWN.earnings, ...(raw.earnings || {}) },
    employer_contributions: { ...EMPTY_BREAKDOWN.employer_contributions, ...(raw.employer_contributions || {}) },
    deductions: { ...EMPTY_BREAKDOWN.deductions, ...(raw.deductions || {}) },
    monthly: { ...EMPTY_BREAKDOWN.monthly, ...(raw.monthly || {}) },
  };
  const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  return (
    <div className="print-area bg-white text-slate-900 rounded-xl border border-slate-200 overflow-hidden" data-testid="offer-letter-container">
      <div className="p-8 sm:p-10">
        {/* Letterhead */}
        <div className="flex items-center justify-between border-b-2 border-slate-900 pb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-white border border-slate-200 overflow-hidden flex items-center justify-center">
              <img src="/logo.jpg" alt="Skanovix logo" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="font-heading font-extrabold text-lg tracking-tight">Skanovix Info Tech Solutions</p>
              <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">Human Resources Department</p>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <p>Chinnamushidivada, Sujatha Nagar, Pendurthi</p>
            <p>Visakhapatnam, Andhra Pradesh</p>
            <p>hr@skanovix.com</p>
          </div>
        </div>

        <div className="flex justify-between mt-6 text-sm">
          <div>
            <p className="font-semibold">{offer.candidate_name}</p>
            <p className="text-slate-600">{offer.email}</p>
          </div>
          <p className="text-slate-600">Date: {today}</p>
        </div>

        <h2 className="font-heading text-center text-xl font-extrabold mt-6 mb-4 uppercase tracking-wide">Letter of Offer / Appointment</h2>

        <p className="text-sm leading-relaxed text-slate-700">
          Dear <span className="font-semibold">{offer.candidate_name}</span>,
        </p>
        <p className="text-sm leading-relaxed text-slate-700 mt-3">
          We are delighted to offer you the position of <span className="font-semibold">{offer.designation}</span> in
          our <span className="font-semibold">{offer.department}</span> department at Skanovix Info Tech Solutions,
          with an expected date of joining of <span className="font-semibold">{offer.joining_date || "—"}</span>.
          Your annual Cost to Company (CTC) will be <span className="font-semibold">{fmtINR(offer.annual_ctc)}</span>,
          structured in accordance with the applicable statutory regulations of the Government of India.
        </p>
        {(offer.work_location || offer.reporting_manager || offer.probation_months || offer.notice_period) && (
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-4 text-sm border border-slate-200 rounded-lg p-3" data-testid="offer-terms-block">
            {offer.work_location && <p><span className="text-slate-500">Work Location:</span> <span className="font-semibold">{offer.work_location}</span></p>}
            {offer.reporting_manager && <p><span className="text-slate-500">Reporting Manager:</span> <span className="font-semibold">{offer.reporting_manager}</span></p>}
            {offer.probation_months ? <p><span className="text-slate-500">Probation Period:</span> <span className="font-semibold">{offer.probation_months} months</span></p> : null}
            {offer.notice_period && <p><span className="text-slate-500">Notice Period:</span> <span className="font-semibold">{offer.notice_period}</span></p>}
          </div>
        )}
        {offer.additional_terms && (
          <p className="text-sm leading-relaxed text-slate-700 mt-3 whitespace-pre-line" data-testid="offer-additional-terms">{offer.additional_terms}</p>
        )}

        {/* CTC Breakdown */}
        <h3 className="font-heading font-bold mt-6 mb-2 text-sm uppercase tracking-wide">Annexure A — Salary Structure (Annual)</h3>
        <div className="border border-slate-200 rounded-lg overflow-hidden text-sm divide-y divide-slate-100">
          <div className="bg-slate-900 text-white px-3 py-2 font-semibold flex justify-between"><span>A. Fixed Earnings</span><span>Amount / Year</span></div>
          <Row label="Basic Salary (50% of CTC)" value={fmtINR(b.earnings.basic)} />
          <Row label={`House Rent Allowance (${offer.metro ? "50%" : "40%"} of Basic)`} value={fmtINR(b.earnings.hra)} />
          <Row label="Special Allowance" value={fmtINR(b.earnings.special_allowance)} />
          <Row label="Gross Salary (A)" value={fmtINR(b.gross_annual)} bold />

          <div className="bg-slate-900 text-white px-3 py-2 font-semibold flex justify-between"><span>B. Employer Contributions (over &amp; above CTC)</span><span></span></div>
          <Row label="Provident Fund — Employer (12% of Basic, capped)" value={fmtINR(b.employer_contributions.pf_employer)} />
          <Row label="Group Health Insurance (Medical Cover)" value={fmtINR(b.employer_contributions.health_insurance)} />
          {b.employer_contributions.esi_employer > 0 && <Row label="ESI — Employer (3.25% of Gross)" value={fmtINR(b.employer_contributions.esi_employer)} />}

          <Row label="Annual CTC (Gross Salary)" value={fmtINR(b.annual_ctc)} bold accent />
        </div>

        <h3 className="font-heading font-bold mt-5 mb-2 text-sm uppercase tracking-wide">Statutory Deductions (Employee)</h3>
        <div className="border border-slate-200 rounded-lg overflow-hidden text-sm divide-y divide-slate-100">
          <Row label="Provident Fund — Employee (12%)" value={fmtINR(b.deductions.pf_employee)} />
          {b.deductions.esi_employee > 0 && <Row label="ESI — Employee (0.75%)" value={fmtINR(b.deductions.esi_employee)} />}
          <Row label={`Income Tax / TDS (est., ${b.tax_regime === "old" ? "old" : "new"} regime)`} value={fmtINR(b.deductions.tds)} />
          <Row label="Net Take-Home (Annual)" value={fmtINR(b.net_annual)} bold accent />
          <Row label="Net Take-Home (Monthly)" value={fmtINR(b.net_monthly)} bold />
        </div>

        <p className="text-sm leading-relaxed text-slate-700 mt-6">
          This offer is subject to satisfactory verification of your documents and background. We look forward to
          welcoming you to the Skanovix family.
        </p>

        <h3 className="font-heading font-bold mt-6 mb-2 text-sm uppercase tracking-wide">Annexure B — Non-Disclosure Agreement (NDA)</h3>
        <div className="border border-slate-200 rounded-lg p-4 text-[11px] leading-relaxed text-slate-700 space-y-2">
          <p>
            By accepting this offer, the Employee agrees to the following strict confidentiality terms. During and after
            employment, the Employee shall <strong>not</strong> disclose, copy, reproduce, or use — directly or indirectly —
            any Confidential Information of Skanovix Info Tech Solutions, its clients, or partners.
          </p>
          <p>
            <strong>&ldquo;Confidential Information&rdquo;</strong> includes but is not limited to: source code, algorithms, architecture,
            client data, personally identifiable information, trade secrets, business plans, pricing, financials, employee
            records, credentials, and any proprietary or non-public material, in any form.
          </p>
          <ol className="list-decimal pl-4 space-y-1">
            <li>All work product, inventions and intellectual property created during employment vest solely with the Company.</li>
            <li>Confidential Information must be returned or destroyed upon separation; no copies may be retained.</li>
            <li>Confidentiality obligations survive termination and continue <strong>indefinitely</strong> for trade secrets and for <strong>three (3) years</strong> for other Confidential Information.</li>
            <li>Unauthorised disclosure may result in immediate termination, injunctive relief, and recovery of damages and legal costs.</li>
            <li>This NDA is governed by the laws of India; courts at Bengaluru, Karnataka shall have exclusive jurisdiction.</li>
          </ol>
        </div>

        <h3 className="font-heading font-bold mt-5 mb-2 text-sm uppercase tracking-wide">Annexure C — Background Verification (BGV) Consent</h3>
        <div className="border border-slate-200 rounded-lg p-4 text-[11px] leading-relaxed text-slate-700 space-y-2">
          <p>
            This offer is <strong>strictly contingent</strong> upon successful completion of a background verification. The
            Employee hereby authorises Skanovix Info Tech Solutions and its authorised agencies to verify:
          </p>
          <ol className="list-decimal pl-4 space-y-1">
            <li>Identity and address proof (PAN, Aadhaar, passport, utility records).</li>
            <li>Educational qualifications and professional certifications.</li>
            <li>Previous employment history, designations, tenure and reason for leaving.</li>
            <li>Criminal record and court checks, and where applicable, credit history.</li>
            <li>Reference checks with former employers and listed references.</li>
          </ol>
          <p>
            The Employee confirms that all information provided is <strong>true and complete</strong>. Any
            misrepresentation, falsification, or discrepancy discovered during or after verification shall entitle the
            Company to <strong>withdraw this offer or terminate employment immediately</strong>, without notice or
            liability, and may invite legal action. Submission of required documents (PAN, Aadhaar, UAN, education and
            relieving letters) via the employee portal is mandatory within <strong>7 days</strong> of joining.
          </p>
        </div>

        <p className="text-xs text-slate-700 mt-8 p-3 rounded-lg bg-slate-50 border border-slate-200">
          I, <strong>{offer.candidate_name}</strong>, accept this offer of employment along with the
          <strong> Non-Disclosure Agreement (Annexure B)</strong> and hereby consent to
          <strong> Background Verification (Annexure C)</strong>. I confirm the information I have provided is true and complete.
        </p>

        <div className="flex justify-between items-end mt-10">
          <div className="text-sm">
            <div className="w-40 border-b border-slate-400 mb-1" />
            <p className="font-semibold">Authorised Signatory</p>
            <p className="text-slate-500 text-xs">Head — Human Resources</p>
            <p className="text-slate-500 text-xs">Skanovix Info Tech Solutions</p>
          </div>
          <div className="text-sm text-right">
            <div className="w-40 border-b border-slate-400 mb-1 ml-auto" />
            <p className="font-semibold">Candidate Acceptance</p>
            <p className="text-slate-500 text-xs">{offer.candidate_name}</p>
          </div>
        </div>
        <p className="text-[10px] text-slate-400 mt-8 text-center font-mono">
          Computed as per EPF Act, ESI Act & applicable state Professional Tax slabs.
        </p>
      </div>
    </div>
  );
}
